import React from "react";
import { useQuery } from "react-query";
import { Link } from "react-router-dom";
import {
  Package,
  AlertTriangle,
  TrendingDown,
  BarChart3,
  ArrowRight,
} from "lucide-react";
import { dashboardService } from "@/services/dashboardService";

const InventoryOverview = () => {
  const { data: inventoryData, isLoading } = useQuery(
    "dashboard-inventory-overview",
    dashboardService.getInventoryOverview,
    {
      select: (response) => response.data,
      refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
    }
  );

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-300 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-300 rounded"></div>
            <div className="h-4 bg-gray-300 rounded w-3/4"></div>
            <div className="h-4 bg-gray-300 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-gray-900">
            Inventory Overview
          </h3>
          <Link
            to="/inventory"
            className="text-sm text-primary-600 hover:text-primary-500 flex items-center"
          >
            View all
            <ArrowRight className="h-4 w-4 ml-1" />
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <div className="flex items-center justify-center mb-2">
              <Package className="h-6 w-6 text-blue-600" />
            </div>
            <p className="text-sm text-gray-600">Total Items</p>
            <p className="text-xl font-semibold text-blue-600">
              {inventoryData?.data?.totalItems?.toLocaleString() || "0"}
            </p>
          </div>

          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="flex items-center justify-center mb-2">
              <BarChart3 className="h-6 w-6 text-green-600" />
            </div>
            <p className="text-sm text-gray-600">Total Value</p>
            <p className="text-xl font-semibold text-green-600">
              ₦{inventoryData?.data?.totalValue?.toLocaleString() || "0"}
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
            <div className="flex items-center">
              <AlertTriangle className="h-5 w-5 text-red-600 mr-3" />
              <div>
                <p className="text-sm font-medium text-gray-900">
                  Low Stock Items
                </p>
                <p className="text-xs text-gray-500">Below minimum level</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-lg font-semibold text-red-600">
                {inventoryData?.data?.lowStockItems || 0}
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
            <div className="flex items-center">
              <TrendingDown className="h-5 w-5 text-yellow-600 mr-3" />
              <div>
                <p className="text-sm font-medium text-gray-900">
                  Out of Stock
                </p>
                <p className="text-xs text-gray-500">Zero quantity</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-lg font-semibold text-yellow-600">
                {inventoryData?.data?.outOfStockItems || 0}
              </p>
            </div>
          </div>
        </div>

        {inventoryData?.data?.lowStockList &&
          inventoryData.lowStockList?.length > 0 && (
            <div className="mt-6">
              <h4 className="text-sm font-medium text-gray-900 mb-3">
                Items Requiring Attention
              </h4>
              <div className="space-y-2">
                {inventoryData.lowStockList.slice(0, 4).map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between text-sm"
                  >
                    <span className="text-gray-600 truncate">{item.name}</span>
                    <div className="text-right">
                      <span
                        className={`font-medium ${
                          item.quantity === 0
                            ? "text-red-600"
                            : "text-yellow-600"
                        }`}
                      >
                        {item.quantity} {item.unit}
                      </span>
                      <p className="text-xs text-gray-500">
                        Min: {item.minimum}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              {inventoryData.lowStockList?.length > 4 && (
                <p className="text-xs text-gray-500 mt-2">
                  +{inventoryData.lowStockList?.length - 4} more items
                </p>
              )}
            </div>
          )}

        {inventoryData?.data?.recentMovements &&
          inventoryData.recentMovements?.length > 0 && (
            <div className="mt-6">
              <h4 className="text-sm font-medium text-gray-900 mb-3">
                Recent Movements
              </h4>
              <div className="space-y-2">
                {inventoryData.recentMovements
                  .slice(0, 3)
                  .map((movement, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between text-sm"
                    >
                      <span className="text-gray-600 truncate">
                        {movement.itemName}
                      </span>
                      <div className="text-right">
                        <span
                          className={`font-medium ${
                            movement.type === "in"
                              ? "text-green-600"
                              : "text-red-600"
                          }`}
                        >
                          {movement.type === "in" ? "+" : "-"}
                          {movement.quantity}
                        </span>
                        <p className="text-xs text-gray-500">{movement.date}</p>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

export default InventoryOverview;
